// src/automation/components/TableReportDesignBlock.js
//
// Generic Report Design for table-style reports (VAT Inhouse QSR, KYC
// Concentrix / Sutherland, SVR Sutherland). The task's REPORT_DESIGN schema
// comes from the backend (`report_layout`), so this one editor serves every
// such task:
//   • texts — subtitle / intro / notes / footer (blank = default, or hide)
//   • per table — title, sort order, options (e.g. show empty queues), and
//     columns: order, show/hide, header text
//
// Saves `report` = { texts, texts_hidden, tables: { <id>: { title, columns,
// sort, options } } }. Only changed pieces are written, so anything you don't
// touch keeps the current default.

import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Save, ArrowUp, ArrowDown, AlertTriangle, Table2 } from "lucide-react";
import { getReportLayout } from "../api/automation_data";

function move(list, idx, dir) {
  const j = idx + dir;
  if (j < 0 || j >= list.length) return list;
  const copy = [...list];
  [copy[idx], copy[j]] = [copy[j], copy[idx]];
  return copy;
}

// Default column list for a schema table: schema order, default visibility.
const defaultColumns = (t) =>
  t.columns.map((c) => ({ key: c.key, defaultLabel: c.label, label: "", show: c.default_show !== false }));

function initColumns(t, saved) {
  if (!Array.isArray(saved?.columns) || !saved.columns.length) return defaultColumns(t);
  const byKey = Object.fromEntries(t.columns.map((c) => [c.key, c]));
  const out = [];
  const seen = new Set();
  for (const c of saved.columns) {
    const s = byKey[c?.key];
    if (!s || seen.has(c.key)) continue;
    seen.add(c.key);
    const label = c.label && c.label !== s.label ? c.label : "";
    out.push({ key: c.key, defaultLabel: s.label, label, show: c.show !== false });
  }
  for (const c of t.columns) {
    if (!seen.has(c.key)) out.push({ key: c.key, defaultLabel: c.label, label: "", show: c.default_show !== false });
  }
  return out;
}

function fromLayout(layout) {
  const design = layout.design || { texts: [], tables: [] };
  const rep = layout.report || {};
  const tables = {};
  for (const t of design.tables || []) {
    const saved = rep.tables?.[t.id] || {};
    const options = {};
    for (const o of t.options || []) {
      options[o.key] = saved.options && o.key in saved.options ? !!saved.options[o.key] : !!o.default;
    }
    tables[t.id] = {
      title: saved.title || "",
      sort: saved.sort || t.sort_default || "",
      options,
      columns: initColumns(t, saved),
    };
  }
  return {
    texts: { ...(rep.texts || {}) },
    hidden: Array.isArray(rep.texts_hidden) ? [...rep.texts_hidden] : [],
    tables,
  };
}

export default function TableReportDesignBlock({ taskName, config, canEdit, onSave }) {
  const { data: layout, isLoading, error, refetch } = useQuery({
    queryKey: ["reportLayout", taskName],
    queryFn: () => getReportLayout(taskName),
    enabled: !!taskName,
    staleTime: 5 * 60 * 1000,
  });
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (layout && !draft) setDraft(fromLayout(layout));
  }, [layout, draft]);

  if (isLoading) return <div className="auto-empty">Loading report design…</div>;
  if (error) {
    return (
      <div className="auto-eb-alert auto-eb-alert--err">
        <AlertTriangle size={13} /> <span>{error.message}</span>
      </div>
    );
  }
  if (!draft) return null;

  const design = layout.design || { texts: [], tables: [] };
  const readOnly = !canEdit;

  const setText = (key, value) => setDraft((d) => ({ ...d, texts: { ...d.texts, [key]: value } }));
  const toggleHidden = (key) =>
    setDraft((d) => ({
      ...d,
      hidden: d.hidden.includes(key) ? d.hidden.filter((k) => k !== key) : [...d.hidden, key],
    }));
  const updTable = (id, patch) =>
    setDraft((d) => ({ ...d, tables: { ...d.tables, [id]: { ...d.tables[id], ...patch } } }));
  const updColumn = (id, i, patch) =>
    setDraft((d) => {
      const cols = d.tables[id].columns.map((c, j) => (j === i ? { ...c, ...patch } : c));
      return { ...d, tables: { ...d.tables, [id]: { ...d.tables[id], columns: cols } } };
    });

  const buildReport = () => {
    const report = {};
    const texts = Object.fromEntries(
      Object.entries(draft.texts).filter(([, v]) => typeof v === "string" && v.trim()).map(([k, v]) => [k, v.trim()])
    );
    if (Object.keys(texts).length) report.texts = texts;
    if (draft.hidden.length) report.texts_hidden = draft.hidden;

    const tables = {};
    for (const t of design.tables || []) {
      const cur = draft.tables[t.id];
      const obj = {};
      if (cur.title.trim()) obj.title = cur.title.trim();
      if (cur.sort && cur.sort !== t.sort_default) obj.sort = cur.sort;
      const opts = {};
      for (const o of t.options || []) if (!!cur.options[o.key] !== !!o.default) opts[o.key] = !!cur.options[o.key];
      if (Object.keys(opts).length) obj.options = opts;
      const def = defaultColumns(t);
      const changed =
        cur.columns.length !== def.length ||
        cur.columns.some((c, i) => c.key !== def[i].key || c.show !== def[i].show || c.label.trim());
      if (changed) {
        obj.columns = cur.columns.map((c) => ({
          key: c.key,
          ...(c.label.trim() ? { label: c.label.trim() } : {}),
          show: c.show,
        }));
      }
      if (Object.keys(obj).length) tables[t.id] = obj;
    }
    if (Object.keys(tables).length) report.tables = tables;
    return report;
  };

  const handleSave = async () => {
    if ((design.tables || []).some((t) => !draft.tables[t.id].columns.some((c) => c.show))) {
      setNotice({ type: "err", msg: "Each table needs at least one visible column." });
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const report = buildReport();
      const next = { ...config };
      if (Object.keys(report).length) next.report = report;
      else delete next.report;
      await onSave(next);
      const fresh = await refetch();
      if (fresh.data) setDraft(fromLayout(fresh.data));
      setNotice({ type: "ok", msg: "Report design saved. Run a Test to preview it." });
    } catch (e) {
      setNotice({ type: "err", msg: e?.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDraft(fromLayout({ design, report: {} }));
    setNotice({ type: "ok", msg: "Reset to defaults (not saved yet). Click Save to apply." });
  };

  return (
    <div className="auto-rd">
      {notice && (
        <div className={`auto-eb-alert ${notice.type === "err" ? "auto-eb-alert--err" : "auto-eb-alert--ok"}`}>
          <span>{notice.msg}</span>
        </div>
      )}

      {(design.texts || []).length > 0 && (
        <section className="auto-rd-sec">
          <div className="auto-rd-sechead">
            <h5 className="auto-rd-h">Text</h5>
            <span className="auto-rd-hint">Blank = default · tick Hide to remove a line</span>
          </div>
          <div className="auto-rd-grid2">
            {design.texts.map((tx) => {
              const hidden = draft.hidden.includes(tx.key);
              return (
                <label className="auto-rd-field" key={tx.key}>
                  <span>
                    {tx.label}
                    {tx.placeholders?.length ? ` · ${tx.placeholders.join(" ")}` : ""}
                  </span>
                  <div className="auto-td-textrow">
                    <input
                      className="auto-eb-input" disabled={readOnly || hidden}
                      value={draft.texts[tx.key] || ""}
                      placeholder={hidden ? "(hidden)" : tx.default || "(none)"}
                      onChange={(e) => setText(tx.key, e.target.value)}
                    />
                    <label className="auto-eb-toggle auto-td-hide">
                      <input type="checkbox" disabled={readOnly} checked={hidden} onChange={() => toggleHidden(tx.key)} />
                      <span>Hide</span>
                    </label>
                  </div>
                </label>
              );
            })}
          </div>
        </section>
      )}

      {(design.tables || []).map((t) => {
        const cur = draft.tables[t.id];
        return (
          <section className="auto-rd-sec" key={t.id}>
            <div className="auto-rd-table-head">
              <Table2 size={14} className="auto-rd-table-ic" />
              <h5 className="auto-rd-h">{t.label}</h5>
            </div>
            <div className="auto-rd-grid2">
              {"title_default" in t && (
                <label className="auto-rd-field">
                  <span>Title{t.title_placeholders?.length ? ` · ${t.title_placeholders.join(" ")}` : ""}</span>
                  <input
                    className="auto-eb-input" disabled={readOnly} value={cur.title}
                    placeholder={t.title_default} onChange={(e) => updTable(t.id, { title: e.target.value })}
                  />
                </label>
              )}
              {(t.sort_options || []).length > 0 && (
                <label className="auto-rd-field">
                  <span>Sort rows by</span>
                  <select
                    className="auto-eb-input" disabled={readOnly} value={cur.sort}
                    onChange={(e) => updTable(t.id, { sort: e.target.value })}
                  >
                    {t.sort_options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}{o.value === t.sort_default ? " (default)" : ""}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {(t.options || []).map((o) => (
                <label key={o.key} className="auto-eb-toggle">
                  <input
                    type="checkbox" disabled={readOnly} checked={!!cur.options[o.key]}
                    onChange={(e) => updTable(t.id, { options: { ...cur.options, [o.key]: e.target.checked } })}
                  />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>

            <div className="auto-rd-hint auto-td-colhint">Columns — order, show/hide, header text</div>
            <div className="auto-td-cols">
              {cur.columns.map((c, i) => (
                <div key={c.key} className={`auto-td-col ${c.show ? "" : "auto-td-col--off"}`}>
                  <span className="auto-rd-ord">{i + 1}</span>
                  <label className="auto-eb-toggle" title={c.show ? "Shown" : "Hidden"}>
                    <input
                      type="checkbox" disabled={readOnly} checked={c.show}
                      onChange={(e) => updColumn(t.id, i, { show: e.target.checked })}
                    />
                  </label>
                  <input
                    className="auto-eb-input auto-td-col-in" disabled={readOnly || !c.show}
                    value={c.label} placeholder={c.defaultLabel}
                    onChange={(e) => updColumn(t.id, i, { label: e.target.value })}
                  />
                  {!readOnly && (
                    <span className="auto-rd-actions">
                      <button className="auto-rd-ic" title="Move left/up" disabled={i === 0}
                        onClick={() => updTable(t.id, { columns: move(cur.columns, i, -1) })}><ArrowUp size={11} /></button>
                      <button className="auto-rd-ic" title="Move right/down" disabled={i === cur.columns.length - 1}
                        onClick={() => updTable(t.id, { columns: move(cur.columns, i, 1) })}><ArrowDown size={11} /></button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {!readOnly && (
        <div className="auto-rd-footer">
          <button className="auto-eb-btn" onClick={handleReset} disabled={saving}>Reset to defaults</button>
          <button className="auto-eb-btn auto-eb-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <RefreshCw size={13} className="auto-eb-spin" /> : <Save size={13} />} Save report design
          </button>
        </div>
      )}
    </div>
  );
}
